import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SEO } from '../seo/SEO';
import { useI18n } from '../i18n';
import { useAuth } from '../auth';
import { AppHeader } from '../components/AppHeader';
import { useSmartBack } from '../hooks/useSmartBack';
import { TierBadge } from '../components/TierBadge';
import { PresenceChip } from '../components/PresenceChip';
import './LeaderboardPage.css';

const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';
const MEDAL = ['🥇', '🥈', '🥉'];

interface EloEntry {
  rank: number;
  nickname: string;
  user_id: string;
  rating: number;
  peak_rating: number;
  wins: number;
  losses: number;
  current_win_streak?: number;
  has_played_ranked?: boolean;
}

interface SeasonInfo {
  id: number;
  number: number;
  end_date: string;
}

export function LeaderboardPage() {
  const { t } = useI18n();
  const { token, user } = useAuth();
  const smartBack = useSmartBack('/');
  const [entries, setEntries] = useState<EloEntry[]>([]);
  const [season, setSeason] = useState<SeasonInfo | null>(null);
  const [myRank, setMyRank] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [animKey, setAnimKey] = useState(0);
  const [searchParams] = useSearchParams();
  const initialScope = searchParams.get('scope') === 'friends' ? 'friends' : 'global';
  const [scope, setScope] = useState<'global' | 'friends'>(initialScope);

  useEffect(() => {
    setLoading(true);
    setAnimKey(k => k + 1);
    let url: string;
    if (scope === 'friends' && token) {
      url = `${API}/stats/season/leaderboard/friends?limit=200`;
    } else {
      url = token
        ? `${API}/stats/season/leaderboard?limit=200`
        : `${API}/stats/season/leaderboard/public?limit=200`;
    }
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(url, { headers })
      .then(r => r.ok ? r.json() : { season: null, leaderboard: [] })
      .then(data => {
        setSeason(data.season ?? null);
        setEntries(data.leaderboard ?? []);
        setMyRank(data.my_rank ?? null);
      })
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [token, scope]);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getFullYear()}`;
  };

  const podium = entries.slice(0, 3);
  const rest = entries.slice(3);

  return (
    <div className="leaderboard-page">
      <SEO path="/leaderboard" />

      <AppHeader onBack={smartBack} backLabel={t.common.backToHome} title={t.profile.leaderboard} />

      {/* Hero */}
      <div className="lb-hero">
        <h1 className="lb-hero-title">{t.profile.lbEloTitle}</h1>
        <div style={{ display: 'flex', justifyContent: 'center', margin: '6px 0' }}>
          <PresenceChip />
        </div>
        {season && (
          <p className="lb-season-badge">
            {t.seasonBanner.seasonLabel.replace('{n}', String(season.number))}
            <span className="lb-season-sep">·</span>
            {t.profile.seasonUntil.replace('{date}', formatDate(season.end_date))}
          </p>
        )}
        {myRank && (
          <p className="lb-my-rank">
            {t.profile.lbMyRank}: <strong>#{myRank}</strong>
          </p>
        )}
      </div>

      {token && (
        <div className="lb-scope-tabs" role="tablist">
          <button
            role="tab"
            aria-selected={scope === 'global'}
            className={`lb-scope-tab ${scope === 'global' ? 'is-active' : ''}`}
            onClick={() => setScope('global')}
          >
            🌍 {(t.profile as any).lbScopeGlobal ?? 'Global'}
          </button>
          <button
            role="tab"
            aria-selected={scope === 'friends'}
            className={`lb-scope-tab ${scope === 'friends' ? 'is-active' : ''}`}
            onClick={() => setScope('friends')}
          >
            👥 {t.profile.lbScopeFriends}
          </button>
        </div>
      )}

      {loading ? (
        <p className="lb-loading">{t.common.loading}</p>
      ) : !season ? (
        <div className="lb-empty-state">
          <span className="lb-empty-icon">⚔️</span>
          <p>{t.profile.lbNoSeason}</p>
        </div>
      ) : entries.length === 0 ? (
        <div className="lb-empty-state">
          <span className="lb-empty-icon">🏆</span>
          <p>{t.profile.lbEmpty}</p>
        </div>
      ) : (
        <div key={animKey} className="lb-entries">

          {/* Top 3 — Podium (sadece ranked oynamış olanlar podyumda) */}
          {podium.map((entry, i) => (
            <div
              key={`pod-${entry.user_id}`}
              className={`lb-podium-card ${i === 0 ? 'gold' : i === 1 ? 'silver' : 'bronze'} ${entry.user_id === user?.id ? 'is-me' : ''} ${!entry.has_played_ranked ? 'is-unranked' : ''}`}
              style={{ animationDelay: `${i * 120}ms` }}
            >
              <div className="lb-pc-left">
                <span className="lb-pc-medal">{MEDAL[i]}</span>
                <div className="lb-pc-info">
                  <span className="lb-pc-name">
                    {entry.nickname}
                    {(entry.current_win_streak ?? 0) >= 3 && (
                      <span className="lb-streak" title={`${entry.current_win_streak} maçtır kazanıyor`}>
                        🔥{entry.current_win_streak}
                      </span>
                    )}
                  </span>
                  <span className="lb-pc-meta">
                    <TierBadge rating={entry.rating} size="sm" />
                    <span className="lb-pc-wr">{entry.wins}G {entry.losses}M</span>
                  </span>
                </div>
              </div>
              <div className="lb-pc-right">
                <span className="lb-pc-score">
                  {entry.rating}
                  <span className="lb-pc-unit"> elo</span>
                </span>
                {entry.peak_rating > entry.rating && (
                  <span className="lb-pc-peak">↑{entry.peak_rating}</span>
                )}
              </div>
            </div>
          ))}

          {/* 4+ — Liste */}
          {rest.length > 0 && (
            <div className="lb-rest">
              {rest.map((entry, i) => (
                <div
                  key={`rest-${entry.user_id}`}
                  className={`lb-rest-row ${entry.user_id === user?.id ? 'is-me' : ''} ${!entry.has_played_ranked ? 'is-unranked' : ''}`}
                  style={{ animationDelay: `${(i + 3) * 40}ms` }}
                >
                  <span className="lb-rest-rank">#{entry.rank}</span>
                  <span className="lb-rest-name">
                    {entry.nickname}
                    {(entry.current_win_streak ?? 0) >= 3 && (
                      <span className="lb-streak" title={`${entry.current_win_streak} maçtır kazanıyor`}>
                        🔥{entry.current_win_streak}
                      </span>
                    )}
                  </span>
                  <TierBadge rating={entry.rating} size="sm" showName={false} />
                  <span className="lb-rest-wr">{entry.wins}G {entry.losses}M</span>
                  <span className="lb-rest-score">{entry.rating} elo</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
